import { useState } from 'react';
import { FormInputField } from '@src/components/custom/FormInputField';
import {
  BaseDialog,
  DialogClose,
  DialogFooter,
} from '@src/components/custom/BaseDialog';
import { TrashIcon } from '@radix-ui/react-icons';
import { cn } from '@repo/ui/utils';
import { useDeleteUniversity } from '@src/api/useUniversities';
import { useToast } from '@repo/ui/components/useToast';
import { Button } from '@repo/ui/components/Button';
import { useIsMobile } from '@src/hooks/useIsMobile';

export interface DeleteUniversityDialogProps {
  universityName: string;
  universityId: string;
  onDeleted: () => void;
}

export function DeleteUniversityDialog({
  universityName,
  universityId,
  onDeleted,
}: DeleteUniversityDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const deleteUniversity = useDeleteUniversity();

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setError(null);

    if (name !== universityName) {
      setError('The entered name does not match the university name.');
      return;
    }

    try {
      await deleteUniversity.mutateAsync(universityId);
      setName('');
      setOpen(false);
      toast({
        title: 'The university has been successfully deleted.',
        variant: 'success',
      });
      onDeleted();
    } catch {
      toast({
        title: 'Error deleting university',
        variant: 'destructive',
      });
    }
  };

  return (
    <BaseDialog
      title="Delete Account"
      description={
        <>
          <p>
            Are you sure you want to remove this university? Please be aware
            this action is irreversible. To confirm, please enter the university
            name as it appears below:
          </p>
          <p
            style={{
              marginTop: '12px',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            {' '}
            &quot;{universityName}&quot;
          </p>
        </>
      }
      open={open}
      onOpenChange={(opened) => setOpen(opened)}
      trigger={
        <Button
          aria-label="delete account"
          variant="destructiveGhost"
          className={cn('max-w-max', 'mx-4')}
        >
          <TrashIcon className={cn('h-5', 'w-5', !isMobile && 'mr-2')} />
          {!isMobile && (
            <span className={cn('whitespace-nowrap')}>Delete Account</span>
          )}
        </Button>
      }
    >
      <form onSubmit={handleSubmit}>
        <FormInputField
          label="University name"
          id="university-name-confirm"
          required
          name="name"
          type="text"
          placeholder="University name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={error}
        />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="default">Cancel</Button>
          </DialogClose>
          <Button type="submit" variant="destructive">
            Delete
          </Button>
        </DialogFooter>
      </form>
    </BaseDialog>
  );
}
